import React from "react";
import MonacoEditor from "@monaco-editor/react";

type PageEditorJsonPanelProps = {
	editable: boolean;
	pageJsonDraft: string;
	pageJsonError: string | null;
	onFormat: () => void;
	onChange: (value: string) => void;
	jsonEditorOptions: any;
	/** Optional fixed editor height for embedded use-cases (px or CSS value). */
	editorHeight?: number | string;
};

/** Raw JSON page editing panel with Monaco editor and live preview updates. */
const PageEditorJsonPanel: React.FC<PageEditorJsonPanelProps> = ({
	editable,
	pageJsonDraft,
	pageJsonError,
	onFormat,
	onChange,
	jsonEditorOptions,
	editorHeight
}) => (
	<div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4 flex flex-col h-full min-h-0">
		<div className="flex flex-wrap items-center justify-between gap-3">
			<div>
				<p className="text-xs uppercase tracking-[0.3em] text-slate-400">Page JSON</p>
				<p className="text-sm text-slate-600">
					Paste or edit the page payload directly. Live preview updates while JSON is valid.
				</p>
			</div>
			{editable && (
				<button
					type="button"
					onClick={onFormat}
					disabled={!!pageJsonError}
					className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
				>
					Format JSON
				</button>
			)}
		</div>
		<div
			className="flex-1 min-h-0 overflow-hidden rounded-lg border border-slate-200"
			style={
				editorHeight != null
					? {
							height: typeof editorHeight === "number" ? `${editorHeight}px` : editorHeight,
							minHeight: typeof editorHeight === "number" ? `${editorHeight}px` : editorHeight
					  }
					: undefined
			}
		>
			<MonacoEditor
				height={editorHeight != null ? (typeof editorHeight === "number" ? `${editorHeight}px` : editorHeight) : "100%"}
				language="json"
				theme="vs"
				value={pageJsonDraft}
				options={{ ...jsonEditorOptions, readOnly: !editable }}
				onChange={(value) => onChange(value ?? "")}
			/>
		</div>
		{pageJsonError ? (
			<p className="text-xs font-semibold text-rose-600">JSON not valid.</p>
		) : (
			<p className="text-xs text-slate-500">Valid JSON. Changes apply to the preview instantly.</p>
		)}
	</div>
);

export default PageEditorJsonPanel;
